import { useLinkLogic } from '@/app/navigation/stack-links';
import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { EntityForm } from '@/components/forms/EntityForm';
import { useCurrentUser } from '@/features/users/users.hooks';
import { useEntityForm } from '@/hooks/useEntityForm';
import { createListEvent, CreateListPayload, ListEvent } from '@certhon/models';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { usePostListEventsMutation } from '@/features/lists/lists.hooks';
import { templateCreateLayout } from '@/features/lists/lists.schemas';

export const TemplateCreatePanel = ({
  params: _params,
  path,
  ...panelProps
}: RoutedPanelProps) => {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const makeLink = useLinkLogic();

  const { current: list_id } = useRef(crypto.randomUUID());

  const defaultValues = useMemo(
    () => ({
      name: '',
      tags: [],
    }),
    [],
  );

  const entityForm = useEntityForm({
    layout: templateCreateLayout,
    isLoading: false,
    data: undefined,
    defaultValues,
  });

  const postListEventsMutation = usePostListEventsMutation();

  const handleCancel = () => {
    window.history.back();
    return false;
  };

  return (
    <Panel {...panelProps}>
      <EntityForm
        {...entityForm}
        onCancel={handleCancel}
        onSubmit={entityForm.form.handleSubmit(
          async (formValues) => {
            const payload: CreateListPayload = {
              name: formValues.name,
              werk_id: null,
              tags: ['qa', 'template', ...(formValues.tags ?? [])],
            };

            try {
              const eventsToPost: ListEvent[] = [];

              eventsToPost.push(
                createListEvent('CREATE_LIST', {
                  user_id: user!.recnum,
                  list_id,
                  payload,
                }),
              );

              await postListEventsMutation.mutateAsync(eventsToPost);

              const to = makeLink(path.replace(/new$/, list_id));
              navigate({ to });
            } catch (error) {
              toast.error('Could not create template', {
                description:
                  error instanceof Error ? error.message : 'Unknown error',
              });
            }
          },
          () => {
            // validation errors are shown inline by EntityForm
          },
        )}
      />
    </Panel>
  );
};
